import { reconcileTrainingEvalScenarios } from "@/api";
import type { EvalParamValues } from "./evalParamSpecs";
import { seedParamKey } from "./evalSeeds";

// training-infra 가 소유하는 평가 시나리오의 find-or-create.
//
// 시나리오 이름은 이미지와 파라미터로 결정되는 함수 값이다. 그래서 같은 설정은 제출·사용자와
// 무관하게 하나의 시나리오로 모인다. 평가를 실행하는 두 제품에서도 같다. 이 규칙을 한곳에 두는
// 것이 목적이다. 해시의 두 번째 사본은 점점 달라지고, 알림 없이 한 config 를 두 시나리오로 나눈다.
//
// 요청 하나가 모든 seed 를 다룬다. training-infra 가 쉼표로 구분한 seed 파라미터를 읽어 펼친다.
// 그리고 폼이 빠뜨린 파라미터를 이미지가 공개한 기본값으로 채운다.

export type ReconcileSeedScenariosInput = {
  benchmarkImage: string;
  // 폼 원래 값. seed 파라미터는 아직 쉼표로 구분한 목록이다.
  values: EvalParamValues;
};

export type ReconciledSeedScenario = {
  scenarioId: string;
  name: string;
  params: Readonly<Record<string, string>>;
  // null 이면 선택한 이미지가 seed 파라미터를 공개하지 않는다는 뜻이다.
  seed: number | null;
};

export type ReconciledSeedScenarios = {
  scenarios: ReconciledSeedScenario[];
  // training-infra 의 치명적이지 않은 알림. 공개 metadata 가 없는 이미지이거나, 이미지가 선언하지
  // 않은 파라미터가 있는 경우다. 두 경우 모두 실행 결과가 폼이 뜻하는 것과 다르다.
  warnings: string[];
};

// seed 마다 시나리오 하나. Policy 평가는 seed 를 params 에만 둔다. 연결된 학습 평가는 이 구체적
// seed 로 training-infra 평가 step 도 펼친다.
export async function reconcileSeedScenarios(
  input: ReconcileSeedScenariosInput,
): Promise<ReconciledSeedScenarios> {
  // 값을 문자열로 바꾸지 않고 전송한다. 이름 해시는 `String(value)` 로 계산한다. 그래서 여기서
  // boolean 을 `True` 로 미리 바꾸면, 다른 곳에서 같은 설정을 입력한 경우와 다른 시나리오 이름이
  // 된다.
  const reconciled = await reconcileTrainingEvalScenarios({
    benchmark_image: input.benchmarkImage,
    params: input.values,
  });
  return {
    scenarios: reconciled.items.map((item) => ({
      scenarioId: item.scenario.id,
      name: item.scenario.name,
      params: item.env,
      // seed 가 없는 이미지도 시나리오 하나를 받지만 보고할 seed 는 없다. 이 판정은 제출한 폼이
      // 아니라 확정된 env 에서 읽는다. 그래서 답이 펼치기와 같은 출처에서 나온다.
      seed: seedParamKey(item.env) === null ? null : item.seed,
    })),
    warnings: reconciled.warnings,
  };
}
